// Declarative descriptors for the formatting toolbar (feature 003).
//
// Each control knows how to READ its own state from the live editor
// (`isActive`, `can`) and how to APPLY itself (`run`). The toolbar renders from
// this list and the `useFormattingState` hook derives its snapshot from it, so
// the state shown and the command applied come from the same source and cannot
// drift (spec FR-014).
//
// The set of controls here is a subset of what `editorExtensions` already
// permits — adding a control requires the stored schema to already allow that
// format (spec FR-015). Insert-field is deliberately NOT here: it is an action
// owned by the page, not a format with on/off state.

import { type Editor } from '@tiptap/react';

export type FormattingControlId =
  'bold' | 'italic' | 'underline' | 'h1' | 'h2' | 'h3' | 'bulletList' | 'orderedList';

// Platform-neutral shortcut token. `shortcut-label.ts` renders it as ⌘/⌥/⇧ on
// macOS or Ctrl/Alt/Shift elsewhere (spec FR-011) — the descriptor stays free of
// any platform assumption.
export interface ShortcutToken {
  /** ⌘ on macOS, Ctrl elsewhere. */
  mod: boolean;
  alt?: boolean;
  shift?: boolean;
  /** The display key, e.g. 'B', '1', '8'. */
  key: string;
}

export interface FormattingControl {
  id: FormattingControlId;
  /** Accessible name of the format, e.g. "Bold", "Heading 1". */
  label: string;
  shortcut: ShortcutToken;
  /** Whether the format is in effect across the whole current selection. */
  isActive: (editor: Editor) => boolean;
  /** Whether the command can be applied at the current position. */
  can: (editor: Editor) => boolean;
  /** Apply (toggle) the format. */
  run: (editor: Editor) => void;
}

export const FORMATTING_CONTROLS: readonly FormattingControl[] = [
  {
    id: 'bold',
    label: 'Bold',
    shortcut: { mod: true, key: 'B' },
    isActive: (editor) => editor.isActive('bold'),
    can: (editor) => editor.can().chain().focus().toggleBold().run(),
    run: (editor) => {
      editor.chain().focus().toggleBold().run();
    },
  },
  {
    id: 'italic',
    label: 'Italic',
    shortcut: { mod: true, key: 'I' },
    isActive: (editor) => editor.isActive('italic'),
    can: (editor) => editor.can().chain().focus().toggleItalic().run(),
    run: (editor) => {
      editor.chain().focus().toggleItalic().run();
    },
  },
  {
    id: 'underline',
    label: 'Underline',
    shortcut: { mod: true, key: 'U' },
    isActive: (editor) => editor.isActive('underline'),
    can: (editor) => editor.can().chain().focus().toggleUnderline().run(),
    run: (editor) => {
      editor.chain().focus().toggleUnderline().run();
    },
  },
  {
    id: 'h1',
    label: 'Heading 1',
    shortcut: { mod: true, alt: true, key: '1' },
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
    can: (editor) => editor.can().chain().focus().toggleHeading({ level: 1 }).run(),
    run: (editor) => {
      editor.chain().focus().toggleHeading({ level: 1 }).run();
    },
  },
  {
    id: 'h2',
    label: 'Heading 2',
    shortcut: { mod: true, alt: true, key: '2' },
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
    can: (editor) => editor.can().chain().focus().toggleHeading({ level: 2 }).run(),
    run: (editor) => {
      editor.chain().focus().toggleHeading({ level: 2 }).run();
    },
  },
  {
    id: 'h3',
    label: 'Heading 3',
    shortcut: { mod: true, alt: true, key: '3' },
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
    can: (editor) => editor.can().chain().focus().toggleHeading({ level: 3 }).run(),
    run: (editor) => {
      editor.chain().focus().toggleHeading({ level: 3 }).run();
    },
  },
  {
    id: 'bulletList',
    label: 'Bulleted list',
    shortcut: { mod: true, shift: true, key: '8' },
    isActive: (editor) => editor.isActive('bulletList'),
    can: (editor) => editor.can().chain().focus().toggleBulletList().run(),
    run: (editor) => {
      editor.chain().focus().toggleBulletList().run();
    },
  },
  {
    id: 'orderedList',
    label: 'Numbered list',
    shortcut: { mod: true, shift: true, key: '7' },
    isActive: (editor) => editor.isActive('orderedList'),
    can: (editor) => editor.can().chain().focus().toggleOrderedList().run(),
    run: (editor) => {
      editor.chain().focus().toggleOrderedList().run();
    },
  },
];
